// boundary-ok — only imported from Header.tsx, already a client boundary
import { useTranslations } from 'next-intl';
import type { ReactNode, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import CustomLink from 'components/shared/CustomLink';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { RegionSelector } from 'components/shared/RegionSelector';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { UHLogo } from 'assets/icons';
import { HamburgerMorphIcon } from './HamburgerMorphIcon';
import { CartIcon } from './Cart';
import { MarketingBanner } from './MarketingBanner';
import { headerLinkAttribution } from './headerAnalytics';
import type { HeaderTheme } from './theme';
import {
  hasShowcaseIntro,
  isFeaturedCardVisible,
  isMenuLinkVisible,
  isSecondaryCtaVisible,
  resolveLabelKey,
  type FeaturedCard,
  type MenuSection,
  type NavItem,
  type NavMenuItem,
} from './navConfig';
import themeStyles from './theme.module.css';
import styles from './MobileDrawer.module.css';

/**
 * The <900px navigation surface: a full-height sheet with a two-level track
 * (root tabs → one tab's panel). It carries its own theme vars because it
 * renders OUTSIDE the header wrapper, and its own `<MarketingBanner />` so the
 * open drawer's top row is pixel-identical to the closed bar (parity with main,
 * MobileDrawer.tsx:157).
 *
 * The close button uses `header.closeMenu`; the bar's hamburger carries the
 * stable `header.navigationMenu` and conveys state through `aria-expanded`, so
 * the two no longer collide in the a11y tree (HEADER_V2_MIGRATION.md §2 M-1).
 * Every link spreads `headerLinkAttribution()` — GTM trigger + click event,
 * both dropped upstream (§2 A3). Labels come from `t()`, not hardcoded English
 * (§2 A11).
 */
export type MobileDrawerProps = {
  open: boolean;
  onClose: () => void;
  items: NavItem[];
  /**
   * Secondary company/audience link groups, rendered as a visually demoted zone
   * below the product tabs.
   */
  moreSections?: MenuSection[];
  /** Drives the region selector / cart icon variants. */
  theme: HeaderTheme;
  /**
   * The bar's mobile CTA(s), so the drawer's top row is IDENTICAL to the closed
   * bar's right cluster — nothing shifts and the CTA never disappears.
   */
  topCta?: ReactNode;
  triggerRef: RefObject<HTMLButtonElement | null>;
};

const Chevron = ({ direction }: { direction: 'left' | 'right' }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden="true"
  >
    <path
      d={direction === 'right' ? 'M5 3l4 4-4 4' : 'M9 3L5 7l4 4'}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const cx = (...parts: (string | false | undefined)[]) =>
  parts.filter(Boolean).join(' ');

export const MobileDrawer = ({
  open,
  onClose,
  items,
  moreSections = [],
  theme,
  topCta,
  triggerRef,
}: MobileDrawerProps) => {
  const t = useTranslations('common');
  const { region } = useRegionLocale();
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const dialogRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const rootItemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const registerRootItemRef = (key: string, el: HTMLElement | null) => {
    if (el) rootItemRefs.current.set(key, el);
    else rootItemRefs.current.delete(key);
  };
  const wasOpenRef = useRef(false);
  const prevActiveKeyRef = useRef<string | null>(null);

  // Lock body scroll while open + reset to the root list when closed.
  useEffect(() => {
    if (!open) {
      setActiveKey(null);
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Initial focus on open; return focus to the trigger on close.
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      closeButtonRef.current?.focus();
    } else if (wasOpenRef.current) {
      wasOpenRef.current = false;
      triggerRef.current?.focus();
    }
  }, [open, triggerRef]);

  // Focus the back button when entering a sub-panel, and the root item that
  // opened it when coming back.
  useEffect(() => {
    if (!open) return;
    if (activeKey !== null && prevActiveKeyRef.current === null) {
      backButtonRef.current?.focus();
    } else if (activeKey === null && prevActiveKeyRef.current !== null) {
      rootItemRefs.current.get(prevActiveKeyRef.current)?.focus();
    }
    prevActiveKeyRef.current = activeKey;
  }, [activeKey, open]);

  // Escape closes; Tab/Shift+Tab cycle focus inside the dialog. The `[inert]`
  // filter keeps the off-screen slide out of the trap.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.closest('[inert]'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const activeItem = activeKey
    ? (items.find(
        (i): i is NavMenuItem => i.kind === 'menu' && i.key === activeKey,
      ) ?? null)
    : null;

  const featured = (activeItem?.featured ?? []).filter((card) =>
    isFeaturedCardVisible(card, region),
  );
  const showIntro = activeItem
    ? hasShowcaseIntro(activeItem, featured.length)
    : false;
  const sections = activeItem?.sections ?? [];
  const headinglessLinks = sections.flatMap((s) =>
    s.headingKey ? [] : s.links.filter((l) => isMenuLinkVisible(l, region)),
  );
  const headedSections = sections.filter((s) => !!s.headingKey);

  return (
    <div
      className={cx(styles.wrap, open && styles.open, themeStyles[theme])}
      aria-hidden={!open}
      inert={!open}
    >
      <aside
        ref={dialogRef}
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-label={t('header.siteNavigation')}
      >
        {/* STATIC top section: on prod the banner + brand row never animate
            (they mirror the header already on screen); only the menu region
            below them expands. */}
        <div className={styles.topSection}>
          <MarketingBanner />
          <div className={styles.topRow}>
            <CustomLink
              href="/"
              aria-label={t('header.goHome')}
              {...headerLinkAttribution({ label: 'logo', onClick: onClose })}
            >
              <UHLogo className={styles.drawerLogo} fill="currentColor" />
            </CustomLink>
            <div className={styles.topActions}>
              {topCta}
              <RegionSelector
                theme={theme === 'dark' ? 'dark' : 'light'}
                compactOnMobile
              />
              <CartIcon iconTheme={theme === 'dark' ? 'dark' : 'light'} />
              <button
                ref={closeButtonRef}
                className={styles.closeButton}
                type="button"
                onClick={onClose}
                aria-label={t('header.closeMenu')}
              >
                {/* Same morph as the bar's hamburger, driven by the drawer's
                    own `open`. The drawer (z 200) covers the header bar
                    (z 110), so THIS is the morph users actually see. */}
                <HamburgerMorphIcon open={open} />
              </button>
            </div>
          </div>
        </div>
        {/* Expanding reveal — only the menu region animates: the sheet sweeps
            down while the content counter-slides, so the menu appears
            progressively unmasked below the static top section. */}
        <div className={styles.revealRegion}>
          <div className={styles.revealSheet}>
            <div className={styles.drawerInner}>
              {activeItem && (
                <div className={styles.backRow}>
                  <button
                    ref={backButtonRef}
                    className={styles.backButton}
                    type="button"
                    onClick={() => setActiveKey(null)}
                  >
                    <Chevron direction="left" />
                    <span className={styles.backLabel}>
                      {t(activeItem.labelKey)}
                    </span>
                  </button>
                </div>
              )}
              <div className={cx(styles.track, !!activeItem && styles.atSub)}>
                <div
                  className={styles.slide}
                  aria-hidden={activeItem !== null}
                  inert={activeItem !== null}
                >
                  <nav aria-label={t('header.primaryNav')}>
                    <ul className={styles.rootList}>
                      {items.map((item) =>
                        item.kind === 'link' ? (
                          <li key={item.key}>
                            <CustomLink
                              className={styles.rootLink}
                              href={item.href}
                              {...headerLinkAttribution({ onClick: onClose })}
                            >
                              {t(item.labelKey)}
                            </CustomLink>
                          </li>
                        ) : (
                          <li key={item.key}>
                            <button
                              ref={(el) => registerRootItemRef(item.key, el)}
                              className={styles.rootButton}
                              type="button"
                              aria-expanded={activeKey === item.key}
                              onClick={() => setActiveKey(item.key)}
                            >
                              <span>{t(item.labelKey)}</span>
                              <Chevron direction="right" />
                            </button>
                          </li>
                        ),
                      )}
                      {moreSections.length > 0 && (
                        <li className={styles.moreZone}>
                          {moreSections.map((section, idx) => {
                            const visibleLinks = section.links.filter((l) =>
                              isMenuLinkVisible(l, region),
                            );
                            if (visibleLinks.length === 0) return null;
                            return (
                              <div
                                className={styles.moreGroup}
                                key={section.headingKey ?? idx}
                              >
                                <h3 className={styles.sectionHeading}>
                                  {t(section.headingKey!)}
                                </h3>
                                <ul className={styles.sectionList}>
                                  {visibleLinks.map((link) => (
                                    <li key={link.labelKey}>
                                      <CustomLink
                                        className={styles.moreLink}
                                        href={link.href}
                                        {...headerLinkAttribution({
                                          onClick: onClose,
                                        })}
                                      >
                                        {t(resolveLabelKey(link, region))}
                                      </CustomLink>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            );
                          })}
                        </li>
                      )}
                    </ul>
                  </nav>
                </div>

                <div
                  className={styles.slide}
                  aria-hidden={activeItem === null}
                  inert={activeItem === null}
                >
                  {activeItem && (
                    <nav aria-label={t(activeItem.labelKey)}>
                      <div className={styles.subBody}>
                        {(featured.length > 0 || showIntro) && (
                          <div className={styles.subShowcase}>
                            {showIntro && activeItem.taglineKey && (
                              <div className={styles.subIntro}>
                                <span className={styles.subIntroLabel}>
                                  {t(
                                    activeItem.introLabelKey ??
                                      activeItem.labelKey,
                                  )}
                                </span>
                                <p className={styles.subIntroTagline}>
                                  {t(activeItem.taglineKey)}
                                </p>
                              </div>
                            )}
                            {featured.length > 0 && (
                              <div
                                className={styles.featuredRow}
                                data-count={featured.length}
                                data-layout={
                                  activeItem.key === 'performance-lab'
                                    ? 'tall'
                                    : undefined
                                }
                              >
                                {featured.map((card) =>
                                  activeItem.key === 'performance-lab' ? (
                                    <PerformanceLabDrawerCard
                                      key={card.labelKey}
                                      card={card}
                                      region={region}
                                      onClose={onClose}
                                    />
                                  ) : (
                                    <SquareFeaturedCard
                                      key={card.labelKey}
                                      card={card}
                                      region={region}
                                      onClose={onClose}
                                    />
                                  ),
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {headinglessLinks.length > 0 && (
                          <section className={styles.section}>
                            <ul className={styles.sectionList}>
                              {headinglessLinks.map((link) => (
                                <li key={link.labelKey}>
                                  <CustomLink
                                    className={cx(
                                      styles.sectionLink,
                                      styles.primary,
                                      link.emphasis && styles.emphasis,
                                    )}
                                    href={link.href}
                                    {...headerLinkAttribution({
                                      onClick: onClose,
                                    })}
                                  >
                                    {t(resolveLabelKey(link, region))}
                                  </CustomLink>
                                </li>
                              ))}
                            </ul>
                          </section>
                        )}
                        {headedSections.map((section, idx) => {
                          const visibleLinks = section.links.filter((l) =>
                            isMenuLinkVisible(l, region),
                          );
                          if (visibleLinks.length === 0) return null;
                          return (
                            <section
                              className={styles.section}
                              key={section.headingKey ?? idx}
                            >
                              <h3 className={styles.sectionHeading}>
                                {t(section.headingKey!)}
                              </h3>
                              <ul className={styles.sectionList}>
                                {visibleLinks.map((link) => (
                                  <li key={link.labelKey}>
                                    <CustomLink
                                      className={cx(
                                        styles.sectionLink,
                                        link.emphasis && styles.emphasis,
                                      )}
                                      href={link.href}
                                      {...headerLinkAttribution({
                                        onClick: onClose,
                                      })}
                                    >
                                      {t(resolveLabelKey(link, region))}
                                    </CustomLink>
                                  </li>
                                ))}
                              </ul>
                            </section>
                          );
                        })}
                      </div>
                    </nav>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

const SquareFeaturedCard = ({
  card,
  region,
  onClose,
}: {
  card: FeaturedCard;
  region: string;
  onClose: () => void;
}) => {
  const t = useTranslations('common');
  const labelKey = resolveLabelKey(card, region);
  return (
    <CustomLink
      className={styles.featuredCard}
      href={card.href}
      {...headerLinkAttribution({
        onClick: onClose,
        buttonType: card.buttonType,
      })}
    >
      <span className={styles.featuredImageWrap}>
        <CustomImage
          src={card.imageMobile ?? card.image}
          alt={card.imageAlt ?? t(labelKey)}
          fill
          sizes="50vw"
          style={{
            objectFit:
              card.imageFit ?? (card.imageMobile ? 'cover' : 'contain'),
          }}
        />
      </span>
      <span className={styles.featuredLabel}>
        <span>{t(labelKey)}</span>
        <span className={styles.featuredCaret} aria-hidden>
          ›
        </span>
      </span>
    </CustomLink>
  );
};

const PerformanceLabDrawerCard = ({
  card,
  region,
  onClose,
}: {
  card: FeaturedCard;
  region: string;
  onClose: () => void;
}) => {
  const t = useTranslations('common');
  const labelKey = resolveLabelKey(card, region);
  const label = t(labelKey);
  const rawCities = card.citiesKey ? t.raw(card.citiesKey) : [];
  const cities = Array.isArray(rawCities) ? rawCities : [];
  const secondaryCta =
    card.secondaryCta && isSecondaryCtaVisible(card.secondaryCta, region)
      ? card.secondaryCta
      : undefined;

  return (
    <article
      className={styles.tallCard}
      aria-label={
        card.comingSoon
          ? t('header.v3.menu.performanceLab.comingSoonAria')
          : undefined
      }
    >
      <span className={styles.tallImageWrap}>
        <CustomImage
          src={card.imageMobile ?? card.image}
          alt={card.imageAlt ?? label}
          fill
          sizes="calc(100vw - 32px)"
          style={{ objectFit: card.imageFit ?? 'cover' }}
        />
      </span>
      <div className={styles.tallBody}>
        {card.comingSoon ? (
          <>
            <span className={styles.tallTitle}>{label}</span>
            {cities.length > 0 && (
              <ul className={styles.tallCities}>
                {cities.map((city) => (
                  <li key={city}>{city}</li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            <span className={styles.tallTitle}>{label}</span>
            {(card.descriptionKey || card.footnoteKey) && (
              <span className={styles.tallDesc}>
                {card.descriptionKey ? t(card.descriptionKey) : null}
                {card.footnoteKey
                  ? `${card.descriptionKey ? '\n' : ''}${t(card.footnoteKey)}`
                  : null}
              </span>
            )}
          </>
        )}
        {card.ctaKey && (
          <div className={styles.tallCtas}>
            <CustomLink
              href={card.href}
              className={cx(
                styles.tallCta,
                card.comingSoon && styles.tallCtaWide,
              )}
              {...headerLinkAttribution({
                onClick: onClose,
                buttonType: card.buttonType,
              })}
            >
              {t(card.ctaKey)}
            </CustomLink>
            {secondaryCta && (
              <CustomLink
                href={secondaryCta.href}
                className={styles.tallCta}
                {...headerLinkAttribution({
                  onClick: onClose,
                  buttonType: secondaryCta.buttonType,
                })}
              >
                {t(secondaryCta.labelKey)}
              </CustomLink>
            )}
          </div>
        )}
      </div>
    </article>
  );
};
